import type { SelectOptionWithMetadata } from '@lib/responses/configurator';
import {
  ConfiguratorOperationTypeOptionMeta,
  mapVisualToSnapshotOperationType,
} from '@snap-types/features/configurator-v1/types/snapshot/operation-type';
import type { PaginationParams } from '@snap-types/models/pagination';
import { visualConfiguratorOperationTypeSchema } from '@snap-types/models/visual/operation-type';
import {
  createFieldKey,
  findStaticOption,
  updateField,
} from '@ui/features/configurator/store';
import type {
  ConfiguratorDynamicFieldDefinition,
  ConfiguratorFieldState,
  ConfiguratorOperationTypeFieldType,
  ConfiguratorStoreAction,
} from '@ui/features/configurator/types';
import { configuratorService } from '@ui/services';

import type { SelectedItem } from '../../components/inputs/BaseSingleChoiceInput';
import { SCRAP_YIELD_NONE_OPTION } from '../constants/input-sources';
import type { ConfiguratorResourceOptionMeta } from './resource';

export const operationTypeFieldDefinition: ConfiguratorDynamicFieldDefinition<ConfiguratorOperationTypeOptionMeta> =
  {
    endpoints: {
      list: '/configurator/operation-types',
    },

    mapOption: (data) =>
      mapVisualToSnapshotOperationType(
        visualConfiguratorOperationTypeSchema.parse(data),
      ),

    mapOptions: (operationTypes) => {
      return operationTypes.map(operationTypeFieldDefinition.mapOption);
    },

    fetchOne: () => {
      throw new Error('Not implemented');
    },

    search: ({
      siteCode,
      query,
      pagination,
    }: {
      siteCode: string;
      query: string;
      pagination: PaginationParams;
    }) => {
      return configuratorService.getFieldOptions({
        siteCode,
        fieldDefinition: operationTypeFieldDefinition,
        query,
        pagination,
      });
    },
  };

/**
 * Builds a list of field updates based on a change in the selected Operation
 * Type.
 *
 * When an Operation Type changes, populates `resource`, `run`, `run_type`,
 * `scrap_yield_type`, and `scrap_yield_percentage` based on the metadata
 * attached to the selected Operation Type.
 */
export function buildOperationTypeFieldUpdates(
  fields: ConfiguratorFieldState,
  field: ConfiguratorOperationTypeFieldType,
  item: SelectedItem<ConfiguratorOperationTypeOptionMeta> | null,
  resourceOption: SelectOptionWithMetadata<
    string,
    ConfiguratorResourceOptionMeta
  > | null,
): ConfiguratorStoreAction[] {
  /**
   * All of these fields are expected to exist within the same Operation as the
   * Operation Type field that triggered this update, so we derive their field
   * keys using `field.stepKey`.
   */
  return [
    updateField(createFieldKey(field.stepId, 'resource'), resourceOption),
    updateField(createFieldKey(field.stepId, 'run'), item?.meta?.run ?? null),
    updateField(
      createFieldKey(field.stepId, 'run_type'),
      item?.meta?.runType
        ? findStaticOption(
            fields[createFieldKey(field.stepId, 'run_type')],
            fields,
            item.meta.runType,
          )
        : null,
    ),
    updateField(
      createFieldKey(field.stepId, 'scrap_yield_type'),
      findStaticOption(
        fields[createFieldKey(field.stepId, 'scrap_yield_type')],
        fields,
        item?.meta?.scrapYieldType ?? SCRAP_YIELD_NONE_OPTION.value,
      ),
    ),
    updateField(
      createFieldKey(field.stepId, 'scrap_yield_percentage'),
      item?.meta?.scrapYieldPct ? item.meta.scrapYieldPct : null,
    ),
    updateField(
      createFieldKey(field.stepId, 'setup_hours'),
      item?.meta?.setupHrs ? item.meta.setupHrs : null,
    ),
    updateField(
      createFieldKey(field.stepId, 'setup_cost_per_hour'),
      item?.meta?.setupCostPerHr
        ? item.meta.setupCostPerHr
        : resourceOption?.meta?.setupCostPerHr
          ? resourceOption.meta.setupCostPerHr
          : null,
    ),
    updateField(
      createFieldKey(field.stepId, 'setup_burden_per_hour'),
      item?.meta?.burPerHrSetup
        ? item.meta.burPerHrSetup
        : resourceOption?.meta?.burdenPerHrSetup
          ? resourceOption.meta.burdenPerHrSetup
          : null,
    ),
    updateField(
      createFieldKey(field.stepId, 'run_cost_per_hour'),
      item?.meta?.runCostPerHr
        ? item.meta.runCostPerHr
        : resourceOption?.meta?.runCostPerHr
          ? resourceOption.meta.runCostPerHr
          : null,
    ),
    updateField(
      createFieldKey(field.stepId, 'run_burden_per_hour'),
      item?.meta?.burPerHrRun
        ? item.meta.burPerHrRun
        : resourceOption?.meta?.burdenPerHrRun
          ? resourceOption.meta.burdenPerHrRun
          : null,
    ),
  ];
}
